/**
 * i18n bootstrap.
 *
 * Two languages: en (default) + ar (Arabic). Translations live as inline
 * resource bundles so the build is one chunk — Libyan operators on flaky
 * connections shouldn't have to round-trip a translation file before the
 * UI is usable.
 *
 * The detector reads the `payhub.lang` localStorage key (set by the
 * AppShell language switcher). On first visit, it falls back to
 * navigator.language ∈ { ar, ar-* } else en.
 *
 * RTL: when the active language has dir=rtl, the AppShell flips the
 * <html dir> attribute. Tailwind's tailwindcss-rtl plugin rewrites
 * direction-aware utilities accordingly.
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import ar from './locales/ar';
import en from './locales/en';

export const SUPPORTED = [
  { code: 'en', name: 'English', dir: 'ltr' as const },
  { code: 'ar', name: 'العربية', dir: 'rtl' as const },
];

export type LangCode = (typeof SUPPORTED)[number]['code'];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED.map((l) => l.code),
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'payhub.lang',
      caches: ['localStorage'],
    },
    // Each locale is wrapped under the default namespace ('translation')
    // so dotted keys like t('auth.sign_in_title') resolve via keySeparator,
    // not nsSeparator. Without the wrapper, i18next treats the top-level
    // groups (common / auth / nav / ...) as separate namespaces, the
    // default namespace lookup falls through, and t() returns the literal
    // key — which is what users were seeing on /login.
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
  });

export function dirFor(lang: string): 'ltr' | 'rtl' {
  return SUPPORTED.find((l) => l.code === lang)?.dir ?? 'ltr';
}

export default i18n;
